import * as Yup from 'yup';
import { InferType } from 'yup';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState, useCallback } from 'react';
import { FormProvider, RHFTextField } from '@emfood/shared';
import Iconify from '@emfood/shared/src/components/iconify';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Dialog,
  Button,
  Divider,
  Checkbox,
  FormGroup,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';

import { DetailsFavouritesCollectionModel } from 'src/api/models/favourites';
import {
  useDeleteFavouritesItem,
  useAddFavouritesCollection,
  useAddProductToFavouritesCollection,
} from 'src/api/hooks/api-hooks';

interface DialogProps {
  open: boolean;
  onClose: any;
  personalFavouritesCollections?: DetailsFavouritesCollectionModel[];
  sharedFavouritesCollections?: DetailsFavouritesCollectionModel[];
  isFavouritesLoading?: boolean;
  favouritesCollectionsError?: unknown;
  productStockId: string;
  preSelectedFavourites?: string[];
}

export default function AddProductToFavouritesCollectionDialog({
  open,
  onClose,
  personalFavouritesCollections,
  sharedFavouritesCollections = [],
  isFavouritesLoading,
  favouritesCollectionsError,
  productStockId,
  preSelectedFavourites = [],
}: DialogProps) {
  const { mutate: addProductToFavouritesCollection } = useAddProductToFavouritesCollection();
  const { mutate: addFavouritesCollection, isLoading: isCreatingCollection } =
    useAddFavouritesCollection();
  const { mutate: deleteFavouritesItem } = useDeleteFavouritesItem();
  const queryClient = useQueryClient();
  const dialogTitleId = 'favourites-dialog-title';
  const dialogDescriptionId = 'favourites-dialog-description';

  // Shared lists containing this product. Read-only: chefs can't edit shared lists,
  // but we show them so a solid heart isn't left unexplained by the personal lists alone.
  const sharedCollectionsWithProduct = sharedFavouritesCollections.filter((collection) =>
    collection.items.some((item) => item.productStockId === productStockId)
  );

  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>(() => {
    if (preSelectedFavourites.length > 0) {
      return preSelectedFavourites;
    }
    // Initialize with collections that already contain this product
    return (
      personalFavouritesCollections
        ?.filter((c) => c.items.some((item) => item.productStockId === productStockId))
        .map((c) => c.id) || []
    );
  });
  const [showCreateForm, setShowCreateForm] = useState(false);

  const AddFavouritesListSchema = Yup.object().shape({
    name: Yup.string()
      .max(100, 'Favourites list name must be shorter than 100 characters')
      .required('List Name is required'),
  });

  const defaultValues = useMemo(
    () => ({
      name: '',
    }),
    []
  );

  type ValidationSchemaType = InferType<typeof AddFavouritesListSchema>;

  const methods = useForm<ValidationSchemaType>({
    resolver: yupResolver(AddFavouritesListSchema),
    defaultValues,
  });

  const {
    handleSubmit: handleListSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const handleCollectionToggle = (collectionId: string) => {
    setSelectedCollectionIds((prevSelected) =>
      prevSelected.includes(collectionId)
        ? prevSelected.filter((id) => id !== collectionId)
        : [...prevSelected, collectionId]
    );
  };

  const handleAddToCollection = () => {
    if (personalFavouritesCollections) {
      const collectionsToAdd = selectedCollectionIds.filter((collectionId) => {
        const collection = personalFavouritesCollections.find((c) => c.id === collectionId);
        return (
          collection &&
          !collection.items.some((item) => item.productStockId === productStockId)
        );
      });

      const collectionsToRemove = personalFavouritesCollections.filter((collection) => {
        const isInCollection = collection.items.some(
          (item) => item.productStockId === productStockId
        );
        const isSelected = selectedCollectionIds.includes(collection.id);
        return isInCollection && !isSelected;
      });

      if (collectionsToAdd.length === 0 && collectionsToRemove.length === 0) {
        enqueueSnackbar('No changes to save', {
          variant: 'info',
        });
        onClose();
        return;
      }

      collectionsToAdd.forEach((collectionId) => {
        const collection = personalFavouritesCollections.find((c) => c.id === collectionId);
        if (collection) {
          addProductToFavouritesCollection(
            {
              pathParams: {
                collectionId,
              },
              body: {
                productStockId,
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries('favourites');
                queryClient.invalidateQueries('products');
                queryClient.invalidateQueries([
                  'productDetails',
                  { stockId: productStockId },
                ]);
                enqueueSnackbar(`Product added to '${collection.name}' successfully!`, {
                  variant: 'success',
                });
              },
            }
          );
        }
      });

      collectionsToRemove.forEach((collection) => {
        const item = collection.items.find((i) => i.productStockId === productStockId);
        if (item) {
          deleteFavouritesItem(
            {
              pathParams: {
                collectionId: collection.id,
                itemId: item.id,
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries('favourites');
                queryClient.invalidateQueries('products');
                queryClient.invalidateQueries([
                  'productDetails',
                  { stockId: productStockId },
                ]);
                enqueueSnackbar(`Product removed from '${collection.name}' successfully!`, {
                  variant: 'success',
                });
              },
            }
          );
        }
      });

      onClose();
    }
  };

  const isSubmitDisabled = useCallback(() => {
    if (!personalFavouritesCollections) return true;

    const hasCollectionsToAdd = selectedCollectionIds.some((collectionId) => {
      const collection = personalFavouritesCollections.find((c) => c.id === collectionId);
      return (
        collection &&
        !collection.items.some((item) => item.productStockId === productStockId)
      );
    });

    const hasCollectionsToRemove = personalFavouritesCollections.some((collection) => {
      const isInCollection = collection.items.some(
        (item) => item.productStockId === productStockId
      );
      const isSelected = selectedCollectionIds.includes(collection.id);
      return isInCollection && !isSelected;
    });

    return !hasCollectionsToAdd && !hasCollectionsToRemove;
  }, [selectedCollectionIds, personalFavouritesCollections, productStockId]);

  const onSubmitFavouritesList = handleListSubmit(async (data: ValidationSchemaType) => {
    addFavouritesCollection(
      {
        body: data,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Favourites List Added Successfully', { variant: 'success' });
          queryClient.invalidateQueries(['favourites']).then(() => {
            const updatedCollections = queryClient.getQueryData(['favourites']) as any;

            let newCollectionId: string | null = null;
            if (updatedCollections?.personal) {
              const newCollection = updatedCollections.personal.find(
                (col: any) => col.name === data.name
              );
              if (newCollection) {
                newCollectionId = newCollection.id;
              }
            }

            if (newCollectionId) {
              setSelectedCollectionIds((prev) => [...prev, newCollectionId as string]);
            }

            reset();
            setShowCreateForm(false);
          });
        },
      }
    );
  });

  if (isFavouritesLoading)
    return (
      <Dialog open={open} onClose={onClose} aria-labelledby={dialogTitleId}>
        <CircularProgress aria-label="Loading Favourites Lists" />
      </Dialog>
    );

  if (personalFavouritesCollections && !favouritesCollectionsError)
    return (
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
        aria-modal
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntering: () => {
            const titleElement = document.getElementById(dialogTitleId);
            if (titleElement) {
              titleElement.setAttribute('tabIndex', '-1');
              titleElement.focus();
            }
          },
        }}
      >
        {!showCreateForm ? (
          <>
            <DialogTitle id={dialogTitleId}>Edit Favourites</DialogTitle>
            <DialogContent>
              <Typography id={dialogDescriptionId} sx={{ mb: 2 }}>
                Add to or remove from favourites lists:
              </Typography>

              {personalFavouritesCollections && personalFavouritesCollections.length > 0 && (
                <FormGroup sx={{ ml: 3 }}>
                  {personalFavouritesCollections.map(
                    (collection: DetailsFavouritesCollectionModel) => {
                      const isChecked = selectedCollectionIds.includes(collection.id);
                      return (
                        <FormControlLabel
                          key={collection.id}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handleCollectionToggle(collection.id)}
                              aria-label={`${isChecked ? 'Remove from' : 'Add to'} ${collection.name}`}
                            />
                          }
                          label={collection.name}
                        />
                      );
                    }
                  )}
                </FormGroup>
              )}

              {sharedCollectionsWithProduct.length > 0 && (
                <Box>
                  {!!personalFavouritesCollections?.length && <Divider sx={{ my: 2 }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Iconify icon="eva:people-fill" width={18} sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2" id="shared-favourites-heading">
                      Shared lists (view only)
                    </Typography>
                  </Box>
                  <FormGroup sx={{ ml: 3 }} aria-labelledby="shared-favourites-heading">
                    {sharedCollectionsWithProduct.map((collection) => (
                      <FormControlLabel
                        key={collection.id}
                        control={<Checkbox checked disabled />}
                        label={collection.name}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button variant="outlined" onClick={() => setShowCreateForm(true)}>
                + Create New List
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  aria-label="Cancel adding to Favourites List"
                  variant="outlined"
                  color="success"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleAddToCollection}
                  disabled={isSubmitDisabled()}
                >
                  Save Changes
                </Button>
              </Box>
            </DialogActions>
          </>
        ) : (
          <FormProvider methods={methods} onSubmit={onSubmitFavouritesList}>
            <DialogTitle>Create New Favourites List</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Group your favourite products into useful lists to access regularly.
              </Typography>
              <RHFTextField name="name" label="List Name" />
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowCreateForm(false);
                  reset();
                }}
              >
                Back
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting || isCreatingCollection}
              >
                Create List
              </LoadingButton>
            </DialogActions>
          </FormProvider>
        )}
      </Dialog>
    );

  return null;
}
